
import api from './api';
import { Telemetry, AircraftType } from '../types';

export interface TelemetryReplayPoint {
  timestamp: string;
  lat: number;
  lng: number;
  altitude: number | null;
  groundSpeed: number;
  heading: number;
  signalStrength: number;
  batteryLevel: number;
  imei: string;
  movementState?: 'STATIONARY' | 'TAXI' | 'AIRBORNE' | 'UNKNOWN';
  freshnessBucket?: 'FRESH' | 'DELAYED' | 'STALE' | 'OFFLINE' | 'UNKNOWN';
  confidence?: 'HIGH' | 'MEDIUM' | 'LOW';
}

export interface TelemetryReplayPayload {
  tailNumber: string;
  from: string;
  to: string;
  pointCount: number;
  path: TelemetryReplayPoint[];
  segments: Array<{
    movementState: 'STATIONARY' | 'TAXI' | 'AIRBORNE' | 'UNKNOWN';
    startAt: string;
    endAt: string;
    pointCount: number;
    points: TelemetryReplayPoint[];
  }>;
  summary: {
    totalDistanceNm: number;
    airborneMinutes: number;
    taxiMinutes: number;
    maxGroundSpeed: number;
    maxAltitude: number;
    firstSeenAt: string | null;
    lastSeenAt: string | null;
  };
}

export interface TelemetryCycleEvent {
  timestamp: string;
  eventType: string;
  label: string;
  lat: number | null;
  lng: number | null;
  locationLabel?: string | null;
  coordinates?: string | null;
  altitudeFt?: number | null;
  groundSpeedKt?: number | null;
  verticalSpeedFpm?: number | null;
  heading?: number | null;
  count?: number | null;
  durationMinutes?: number | null;
}

export interface TelemetryFlightCycle {
  startAt?: string | null;
  endAt?: string | null;
  offBlock?: TelemetryCycleEvent | null;
  takeoff?: TelemetryCycleEvent | null;
  landing?: TelemetryCycleEvent | null;
  onBlock?: TelemetryCycleEvent | null;
  blockMinutes?: number | null;
  airborneMinutes?: number | null;
  distanceNm?: number | null;
}

export interface TelemetryEventsPayload {
  tailNumber: string;
  from: string;
  to: string;
  pointCount: number;
  eventCount: number;
  cycles: TelemetryFlightCycle[];
  selectedCycle: TelemetryFlightCycle | null;
  events: TelemetryCycleEvent[];
  summary?: Record<string, unknown> | null;
}

export const telemetryApi = {
  /**
   * Fetches latest position for all aircraft
   */
  getSnapshot: async (): Promise<Record<string, Telemetry>> => {
    // GET /api/telemetry/snapshot -> { success, data }
    const response = await api.get('/telemetry/snapshot');
    const data = response.data?.data || {};
    return typeof data === 'object' && data !== null ? data : {};
  },

  /**
   * Fetches telemetry history for a specific aircraft by tail number
   */
  getHistory: async (tailNumber: string, hours: number = 24, limit: number = 100): Promise<any[]> => {
    const response = await api.get(`/telemetry/history/${tailNumber}`, {
      params: { hours, limit },
    });
    return Array.isArray(response.data?.data) ? response.data.data : [];
  },

  getHistoryRange: async (tailNumber: string, from: string, to: string, limit: number = 500): Promise<any[]> => {
    const response = await api.get(`/telemetry/history/${tailNumber}`, {
      params: { from, to, limit },
    });
    return Array.isArray(response.data?.data) ? response.data.data : [];
  },

  getReplay: async (tailNumber: string, from: string, to: string, limit: number = 2000): Promise<TelemetryReplayPayload | null> => {
    const response = await api.get(`/telemetry/replay/${tailNumber}`, {
      params: { from, to, limit },
    });
    const data = response.data?.data ?? null;
    if (!data || typeof data !== 'object') return null;
    return data as TelemetryReplayPayload;
  },

  getEvents: async (tailNumber: string, from: string, to: string, limit: number = 5000): Promise<TelemetryEventsPayload | null> => {
    const response = await api.get(`/telemetry/events/${encodeURIComponent(tailNumber)}`, {
      params: { from, to, limit },
    });
    const data = response.data?.data ?? null;
    if (!data || typeof data !== 'object') return null;
    return data as TelemetryEventsPayload;
  },

  /**
   * v2Track trip metadata (pilot name, destination, origin) for an aircraft.
   * Uses ESN + tripId from telemetry; returns null if not available.
   */
  getTripMetadata: async (tailNumber: string): Promise<{ pilotName?: string; destination?: string; origin?: string; lastLocation?: string } | null> => {
    const response = await api.get(`/telemetry/trip-metadata/${encodeURIComponent(tailNumber)}`);
    const data = response.data?.data ?? null;
    return data && typeof data === 'object' ? data : null;
  },

  /**
   * Updates a specific leg status (Dispatch/Arrive)
   */
  updateLegStatus: async (legId: string, status: string, details?: any) => {
    const response = await api.patch(`/missions/legs/${legId}/status`, { status, ...details });
    return response.data;
  },

  getLegEvents: async (legId: string) => {
    const response = await api.get(`/missions/legs/${legId}/events`);
    return response.data.data || [];
  },

  logLegEvent: async (legId: string, payload: { message: string; type?: string }) => {
    const response = await api.post(`/missions/legs/${legId}/event`, payload);
    return response.data.data;
  }
};
